import React from 'react';
import Dropdown from '@components/dropdown';
import { useLocation } from '@reach/router';
import { MobileMenuItem } from '@components/header/mobile-header.styled';
import {
  LinkContainer,
  StyledLink,
  StyledExternalLink,
  StyledBorderedLink,
} from '@components/header/get-mobile-navbar-links.styled';

// Optimize ID generation
const generateLinkId = (() => {
  let counter = 0;
  return () => {
    const id = `mobile-navbar-link-${counter}`;
    counter += 1;
    return id;
  };
})();

export interface NavbarLinksType {
  link_label: string;
  link_url: {
    url: string;
    target: string;
    document: {
      uid: string;
    } | null;
  };
  dropdown_menu: {
    document: {
      uid: string;
      data: {
        title: {
          text: string;
        };
        body: {
          primary: {
            group_title: {
              text: string;
            };
          };
          items: {
            link: {
              url: string;
              document: {
                uid: string;
              };
            };
            link_label: string;
            description: string;
          }[];
        }[];
      };
    } | null;
  };
}

const renderMobileNavbarLinks = (navbarLinks: NavbarLinksType[]): JSX.Element[] => {
  const location = useLocation();

  const handleKeyPress = (event: React.KeyboardEvent, callback: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  return navbarLinks.map((link) => {
    const { link_label, link_url, dropdown_menu } = link;
    const { document, url, target } = link_url;

    const renderLink = (to: string, isContact = false, isCareers = false) => {
      const isActive = location.pathname === to;
      const linkId = generateLinkId();

      if (isContact) {
        return (
          <StyledBorderedLink
            to={to}
            $isActive={isActive}
            id={linkId}
            aria-current={isActive ? 'page' : undefined}
            tabIndex={0}
            onKeyDown={(e) => {
              handleKeyPress(e, () => {
                window.location.href = to;
              });
            }}
          >
            {link_label}
          </StyledBorderedLink>
        );
      }

      if (isCareers) {
        return (
          <LinkContainer>
            <StyledLink
              to={to}
              $isActive={isActive}
              id={linkId}
              aria-current={isActive ? 'page' : undefined}
              tabIndex={0}
              onKeyDown={(e) => {
                handleKeyPress(e, () => {
                  window.location.href = to;
                });
              }}
            >
              {link_label}
            </StyledLink>
          </LinkContainer>
        );
      }

      return (
        <StyledLink
          to={to}
          $isActive={isActive}
          id={linkId}
          aria-current={isActive ? 'page' : undefined}
          tabIndex={0}
          onKeyDown={(e) => {
            handleKeyPress(e, () => {
              window.location.href = to;
            });
          }}
        >
          {link_label}
        </StyledLink>
      );
    };

    const linkType = (): JSX.Element => {
      if (document) {
        const to = document.uid === 'home' ? '/' : `/${document.uid}`;
        return renderLink(to, to === '/contact', to === '/careers');
      }

      if (dropdown_menu.document) {
        return (
          <Dropdown
            title={dropdown_menu.document.data.title.text}
            data={dropdown_menu.document.data.body}
          />
        );
      }

      const isExternal = target === '_blank';
      return (
        <StyledExternalLink
          href={url}
          target={target ?? '_self'}
          id={generateLinkId()}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          aria-label={isExternal ? `${link_label} (opens in new tab)` : undefined}
          tabIndex={0}
          onKeyDown={(e) => {
            handleKeyPress(e, () => {
              window.open(url, target ?? '_self');
            });
          }}
        >
          {link_label}
        </StyledExternalLink>
      );
    };

    return <MobileMenuItem key={generateLinkId()}>{linkType()}</MobileMenuItem>;
  });
};

export default renderMobileNavbarLinks;
